import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

function UsersManager() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentUser, setCurrentUser] = useState({
    email: '',
    password: '',
    role: 'user',
    is_active: true
  })
  const [isAdmin, setIsAdmin] = useState(false)
  const [currentRole, setCurrentRole] = useState(null)
  const [filterByCodevaro, setFilterByCodevaro] = useState(true)

  useEffect(() => {
    checkAdminStatus()
  }, [])

  useEffect(() => {
    if (isAdmin) {
      fetchUsers()
    }
  }, [isAdmin, filterByCodevaro])

  const isAdminRole = (role) => role === 'admin' || role === 'superadmin'
  const isSuperAdminRole = (role) => role === 'superadmin'

  const checkAdminStatus = async () => {
    try {
      setLoading(true)

      const {
        data: { user }
      } = await supabase.auth.getUser()

      if (!user?.email) {
        setIsAdmin(false)
        return
      }

      const { data: profile, error } = await supabase
        .from('codevaro_users')
        .select('email, role')
        .eq('email', user.email)
        .maybeSingle()

      if (error) {
        console.error('Error checking admin status:', error)
        setIsAdmin(false)
        return
      }

      setCurrentRole(profile?.role || null)
      setIsAdmin(isAdminRole(profile?.role))
    } catch (err) {
      console.error('Error in admin check:', err)
      setIsAdmin(false)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('codevaro_users')
        .select('id, email, role, created_at')
        .order('created_at', { ascending: false })

      if (error) throw error

      const formattedUsers = (data || []).map((user) => ({
        id: user.id,
        email: user.email,
        role: user.role || 'user',
        is_active: true,
        created_at: user.created_at
      }))

      const filteredUsers = filterByCodevaro
        ? formattedUsers.filter(
            (user) => user.email && user.email.toLowerCase().includes('@codevaro.')
          )
        : formattedUsers

      setUsers(filteredUsers)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError('Σφάλμα κατά τη φόρτωση των χρηστών')
    } finally {
      setLoading(false)
    }
  }

  const toggleFilter = () => {
    setFilterByCodevaro((prev) => !prev)
  }

  const openAddModal = () => {
    setCurrentUser({
      email: '',
      password: '',
      role: 'user',
      is_active: true
    })
    setIsModalOpen(true)
    setError(null)
    setSuccess(null)
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setCurrentUser((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      const email = currentUser.email.trim().toLowerCase()
      const password = currentUser.password
      const role = currentUser.role

      if (!email) throw new Error('Το email είναι υποχρεωτικό.')
      if (!password || password.length < 6) {
        throw new Error('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.')
      }

      if (role === 'superadmin' && !isSuperAdminRole(currentRole)) {
        throw new Error('Μόνο ο κύριος διαχειριστής μπορεί να δημιουργήσει superadmin.')
      }

      let authAlreadyExists = false

      const { error: authError } = await supabase.auth.signUp({
        email,
        password
      })

      if (authError) {
        const msg = authError.message?.toLowerCase() || ''
        if (msg.includes('user already registered') || msg.includes('already registered')) {
          authAlreadyExists = true
        } else {
          throw authError
        }
      }

      const { error: upsertError } = await supabase
        .from('codevaro_users')
        .upsert(
          [
            {
              email,
              role,
              created_at: new Date().toISOString()
            }
          ],
          { onConflict: 'email' }
        )

      if (upsertError) throw upsertError

      setIsModalOpen(false)
      setCurrentUser({
        email: '',
        password: '',
        role: 'user',
        is_active: true
      })

      setSuccess(
        authAlreadyExists
          ? 'Ο λογαριασμός Auth υπήρχε ήδη. Ενημερώθηκε μόνο ο ρόλος στο Codevaro.'
          : 'Ο χρήστης δημιουργήθηκε επιτυχώς.'
      )

      await fetchUsers()

      setTimeout(() => {
        setSuccess(null)
      }, 5000)
    } catch (err) {
      console.error('Error creating user:', err)
      setError('Σφάλμα κατά τη δημιουργία του χρήστη: ' + (err.message || 'Άγνωστο σφάλμα'))
    } finally {
      setLoading(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-red-800">
              Δεν έχετε δικαιώματα διαχειριστή
            </h3>
            <div className="mt-2 text-red-700">
              <p>
                Για να αποκτήσετε πρόσβαση σε αυτή τη σελίδα, θα πρέπει να έχετε λογαριασμό
                διαχειριστή. Επικοινωνήστε με τον κύριο διαχειριστή του συστήματος για να σας
                προσθέσει.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Διαχείριση Χρηστών</h1>
        <div className="flex space-x-4">
          <button
            onClick={toggleFilter}
            className={`inline-flex items-center px-4 py-2 border ${
              filterByCodevaro
                ? 'border-blue-600 bg-blue-100 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700'
            } rounded-md shadow-sm text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            <SafeIcon icon={FiIcons.FiFilter} className="mr-2" />
            {filterByCodevaro ? 'Μόνο codevaro emails' : 'Όλοι οι χρήστες'}
          </button>

          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <SafeIcon icon={FiIcons.FiUserPlus} className="mr-2" />
            Νέος Χρήστης
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {success && (
        <div
          className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <span className="block sm:inline">{success}</span>
        </div>
      )}

      <div className="mt-6">
        {loading && !isModalOpen && (
          <div className="flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        )}

        {!loading && users.length === 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 text-center">
            <SafeIcon icon={FiIcons.FiUsers} className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Δεν υπάρχουν χρήστες</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filterByCodevaro
                ? 'Δεν βρέθηκαν χρήστες με codevaro email.'
                : 'Ξεκινήστε προσθέτοντας έναν νέο χρήστη.'}
            </p>
          </div>
        )}

        {!loading && users.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Χρήστης
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ρόλος
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Κατάσταση
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Δημιουργήθηκε
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Ενέργειες</span>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id || user.email}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                            <SafeIcon icon={FiIcons.FiUser} className="h-5 w-5" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.email || 'Χωρίς email'}
                          </div>
                          <div className="text-xs text-gray-500">ID: {user.id || 'N/A'}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === 'superadmin'
                            ? 'bg-purple-100 text-purple-800'
                            : user.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.role === 'superadmin'
                          ? 'Κύριος Διαχειριστής'
                          : user.role === 'admin'
                          ? 'Διαχειριστής'
                          : 'Χρήστης'}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Ενεργός
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString('el-GR')
                        : 'Μη διαθέσιμο'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => alert('Η λειτουργία είναι προσωρινά απενεργοποιημένη')}
                        className="text-red-600 hover:text-red-900"
                      >
                        Διαγραφή
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Προσθήκη Νέου Χρήστη
                      </h3>

                      <div className="mt-4 space-y-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            required
                            value={currentUser.email}
                            onChange={handleInputChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Κωδικός
                          </label>
                          <input
                            type="password"
                            name="password"
                            id="password"
                            required
                            value={currentUser.password}
                            onChange={handleInputChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                            Ρόλος
                          </label>
                          <select
                            id="role"
                            name="role"
                            required
                            value={currentUser.role}
                            onChange={handleInputChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="user">Χρήστης</option>
                            <option value="admin">Διαχειριστής</option>
                            {isSuperAdminRole(currentRole) && (
                              <option value="superadmin">Κύριος Διαχειριστής</option>
                            )}
                          </select>
                        </div>

                        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
                          Αν το email υπάρχει ήδη στο Supabase Auth, δεν θα δημιουργηθεί νέος auth
                          λογαριασμός. Θα ενημερωθεί μόνο ο ρόλος στο Codevaro.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {loading ? (
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2" />
                    ) : (
                      <SafeIcon icon={FiIcons.FiUserPlus} className="mr-2" />
                    )}
                    Προσθήκη
                  </button>

                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersManager